import { useState, useEffect } from "react";
import { post } from "../../utils/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../../customComponents/ConfirmationModal";
import GridModal from "./GridModal";

const GridSection = ({ groups, groupsLoading }) => {
  const [grids, setGrids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchGrids();
  }, []);

  const fetchGrids = async () => {
    setLoading(true);
    try {
      const res = await post("/cms/grid/list", {});
      if (res.status) setGrids(res.data || []);
    } catch {
      toast.error("Failed to load sections");
    } finally {
      setLoading(false);
    }
  };

  const moveUp = async (index) => {
    if (index === 0) return;
    const next = [...grids];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    const reordered = next.map((g, i) => ({ ...g, display_order: i }));
    setGrids(reordered);
    try {
      await post("/cms/grid/reorder", {
        items: reordered.map((g) => ({ id: g.id, display_order: g.display_order })),
      });
    } catch {
      toast.error("Reorder failed");
      fetchGrids();
    }
  };

  const moveDown = async (index) => {
    if (index === grids.length - 1) return;
    const next = [...grids];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    const reordered = next.map((g, i) => ({ ...g, display_order: i }));
    setGrids(reordered);
    try {
      await post("/cms/grid/reorder", {
        items: reordered.map((g) => ({ id: g.id, display_order: g.display_order })),
      });
    } catch {
      toast.error("Reorder failed");
      fetchGrids();
    }
  };

  const handleDelete = async () => {
    try {
      await post("/cms/grid/delete", { id: deleteTarget.id });
      toast.success("Section deleted");
      setDeleteTarget(null);
      fetchGrids();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openAdd = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (g) => { setEditTarget(g); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); setEditTarget(null); fetchGrids(); };

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-5 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
        <div>
          <h3 className="font-semibold text-gray-900">Sections</h3>
          <p className="text-xs text-gray-400 mt-0.5">Homepage rows that display a set of categories</p>
        </div>
        <button
          onClick={openAdd}
          className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-gray-700 transition-colors"
        >
          + Add
        </button>
      </div>

      {/* Body */}
      <div className="px-5 py-3">
        {loading ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>
        ) : grids.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">No sections yet. Add one to get started.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-100 text-xs uppercase tracking-wide">
                <th className="pb-2 font-medium">Title</th>
                <th className="pb-2 font-medium">Categories</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grids.map((g, i) => (
                <tr key={g.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 text-gray-800 font-medium">{g.title}</td>
                  <td className="py-3 text-gray-500 text-xs">{g.group_ids?.length ?? 0}</td>
                  <td className="py-3">
                    <span className={`text-xs font-medium ${g.is_active ? "text-emerald-600" : "text-gray-400"}`}>
                      {g.is_active ? "● Active" : "● Inactive"}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex gap-1 items-center justify-end">
                      <button onClick={() => moveUp(i)} disabled={i === 0}
                        className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-20">
                        <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_upward</span>
                      </button>
                      <button onClick={() => moveDown(i)} disabled={i === grids.length - 1}
                        className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-20">
                        <span className="material-symbols-outlined" style={{ fontSize: 17 }}>arrow_downward</span>
                      </button>
                      <button
                        onClick={() => openEdit(g)}
                        disabled={groupsLoading}
                        className="ml-1 px-2 py-1 text-blue-600 hover:text-blue-800 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                        title={groupsLoading ? "Loading categories..." : undefined}
                      >
                        Edit
                      </button>
                      <button onClick={() => setDeleteTarget(g)}
                        className="px-2 py-1 text-red-400 hover:text-red-600 text-xs font-medium">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <GridModal
          editTarget={editTarget}
          groups={groups}
          onClose={() => { setModalOpen(false); setEditTarget(null); }}
          onSaved={handleSaved}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleteTarget}
        title="Delete Section"
        message={`Delete "${deleteTarget?.title}"?`}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default GridSection;
